import React, { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { Plus, Pencil, Trash2, ClipboardList, ChevronDown, ChevronUp, MoreVertical, Package, RefreshCw, Search } from 'lucide-react'
import {
  Button, Card, CardContent, Badge, Input,
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../ui'
import { getPlanes, deletePlan, getConceptos, getResumenCargosPlan, recalcularCargosPlan, getDesfaseDePlanes, getCargosFaltantesPlan, asignarPlan } from '../../../lib/supabase'
import { scopeLabel } from '../../../constants/planScope'
import { useConfirmDialog } from '../../../hooks/useConfirmDialog'
import { formatMoney, formatDate } from '../../../utils/helpers'
import PlanEditorWizard from './planEditor/PlanEditorWizard'
import { planListoParaAsignar } from '../../../lib/services/pagosPlanHelpers'

const round2 = (n) => Math.round(Number(n || 0) * 100) / 100

const Stat = ({ label, value }) => (
  <div className="bg-background p-3">
    <p className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</p>
    <p className="mt-0.5 font-semibold tabular-nums">{value}</p>
  </div>
)

const PlanesTab = ({ temporadaId, temporadaSelector = null, temporadas = [] }) => {
  const navigate = useNavigate()
  const { confirm, ConfirmDialog } = useConfirmDialog()
  const [planes, setPlanes] = useState([])
  const [conceptos, setConceptos] = useState([])
  const [loading, setLoading] = useState(true)
  const [editorOpen, setEditorOpen] = useState(false)
  const [editingPlan, setEditingPlan] = useState(null)
  const [expandedPlan, setExpandedPlan] = useState(null)
  const [search, setSearch] = useState('')
  const [applyResult, setApplyResult] = useState(null)
  const [desfase, setDesfase] = useState({})
  const [recalc, setRecalc] = useState({ open: false, plan: null, mode: 'total', preview: null, loading: false, applying: false })

  const load = useCallback(async () => {
    setLoading(true)
    const [planesRes, conceptosRes] = await Promise.all([
      getPlanes({ temporadaId }),
      getConceptos(),
    ])
    const lista = planesRes.data || []
    setPlanes(lista)
    setConceptos((conceptosRes.data || []).filter((c) => c.activo !== false))
    setLoading(false)
    // Que planes cambiaron de monto pero dejaron atras sus cargos. Se calcula al
    // vuelo (una sola consulta) en vez de guardarse: una bandera almacenada se
    // quedaria mintiendo si los montos cambian por otro camino.
    setDesfase(await getDesfaseDePlanes(lista.map((pl) => pl.id), temporadaId))
    return lista
  }, [temporadaId])

  useEffect(() => { load() }, [load])

  // Guardar un plan NO mueve por si solo los cargos ya emitidos. Si cambiaron los
  // montos de un plan que ya le cobra a alguien, se ofrece el ajuste en el acto en
  // vez de dejarlo escondido en un menu: es justo cuando se produce el desfase.
  const onPlanGuardado = async (planId, { recalcular } = {}) => {
    const lista = await load()
    if (!recalcular) return
    const plan = (lista || []).find((pl) => pl.id === planId)
    if (plan) openRecalc(plan, { desdeGuardado: true })
  }

  const totalDesfase = (planId) => {
    const d = desfase[planId] || {}
    return (d.montos || 0) + (d.faltantes || 0)
  }

  // Se nombra el hueco, no un generico: "sin actualizar" (existe con monto viejo)
  // y "sin emitir" (nunca se genero) se arreglan distinto y confundirlos despista.
  const etiquetaDesfase = (planId) => {
    const { montos = 0, faltantes = 0 } = desfase[planId] || {}
    if (montos && faltantes) return `${montos + faltantes} cargos por corregir`
    if (montos) return `${montos} ${montos === 1 ? 'cargo sin actualizar' : 'cargos sin actualizar'}`
    if (faltantes) return `${faltantes} ${faltantes === 1 ? 'cargo sin emitir' : 'cargos sin emitir'}`
    return null
  }

  const openNuevo = () => { setEditingPlan(null); setEditorOpen(true) }
  const openEditar = (plan) => { setEditingPlan(plan); setEditorOpen(true) }

  const handleDeletePlan = async (plan) => {
    const { total, pagados } = await getResumenCargosPlan(plan.id)
    const msg = total > 0
      ? `Este plan tiene ${total} cargo(s) generado(s)${pagados ? ` (${pagados} pagado(s))` : ''}. Eliminar el plan NO borra esas deudas ni los pagos de los jugadores. ¿Continuar?`
      : `Eliminar plan "${plan.nombre}"?`
    if (!await confirm('Eliminar plan', msg, { destructive: true })) return
    await deletePlan(plan.id)
    load()
  }

  // La temporada viaja con el plan: la pantalla de Asignaciones monta en la
  // temporada abierta si no se le dice otra cosa, y ahi el plan de otra
  // temporada se soltaba en silencio. Retirado el boton "Aplicar", este link es
  // la unica puerta de entrada desde Planes.
  const verAsignaciones = (plan) => navigate(
    `/admin/pagos/asignaciones?temporada=${temporadaId}&plan=${plan.id}`,
  )

  const closeRecalc = () => setRecalc({ open: false, plan: null, mode: 'total', preview: null, faltantes: null, loading: false, applying: false, desdeGuardado: false })

  const openRecalc = async (plan, { desdeGuardado = false } = {}) => {
    setRecalc({ open: true, plan, mode: 'total', preview: null, faltantes: null, loading: true, applying: false, desdeGuardado })
    const [{ data }, { data: falt }] = await Promise.all([
      recalcularCargosPlan(plan.id, 'total', { dryRun: true }),
      getCargosFaltantesPlan(plan.id, temporadaId),
    ])
    setRecalc((r) => (r.open && r.plan?.id === plan.id ? { ...r, preview: data, faltantes: falt, loading: false } : r))
  }

  const changeRecalcMode = async (mode) => {
    const planId = recalc.plan?.id
    if (!planId) return
    setRecalc((r) => ({ ...r, mode, loading: true }))
    const { data } = await recalcularCargosPlan(planId, mode, { dryRun: true })
    setRecalc((r) => (r.plan?.id === planId ? { ...r, preview: data, loading: false } : r))
  }

  const applyRecalc = async () => {
    const { plan, mode, faltantes } = recalc
    if (!plan) return
    setRecalc((r) => ({ ...r, applying: true }))

    const { data, error } = await recalcularCargosPlan(plan.id, mode)

    // Emitir lo que nunca se genero. soloFaltantes acota la operacion: no cancela
    // cargos de otros planes ni revive los que el club cancelo a proposito.
    let emitidos = 0
    let errorEmision = null
    if (!error && faltantes?.total > 0) {
      const jugadorIds = [...new Set(faltantes.faltantes.map((f) => f.jugador_id))]
      const res = await asignarPlan({ planId: plan.id, jugadorIds, soloFaltantes: true })
      errorEmision = res.error
      emitidos = res.count || 0
    }

    closeRecalc()
    const fallo = error || errorEmision
    const partes = []
    if (data?.total) partes.push(`${data.total} cargos actualizados`)
    if (emitidos) partes.push(`${emitidos} cargos emitidos`)
    if (data?.saldoAFavor > 0) partes.push(`${formatMoney(data.saldoAFavor)} a saldo a favor`)
    setApplyResult(fallo
      ? { type: 'error', message: 'Error: ' + (fallo.message || fallo) }
      : { type: 'success', message: (partes.join(' · ') || 'Sin cambios') + '.' })
    setTimeout(() => setApplyResult(null), 4500)
    load()
  }

  const totalPlan = (plan) =>
    round2((plan.pagos_planes_items || []).reduce((sum, item) => sum + Number(item.monto || 0), 0))

  const planEstado = (plan) => {
    const total = totalPlan(plan)
    const parc = plan.pagos_planes_parcialidades || []
    const sumaParc = round2(parc.reduce((s, p) => s + Number(p.monto || 0), 0))
    const cargos = plan.pagos_cargos?.[0]?.count || 0
    // Asignado = ya se le asigno a alguien y genero cargos (vivo). Antes de asignar, el estado refleja el cuadre.
    if (cargos > 0) return { label: 'Asignado', cls: 'bg-green-100 text-green-800' }
    if (parc.length === 0) return { label: 'Borrador · sin pagos', cls: 'bg-amber-100 text-amber-800' }
    if (round2(sumaParc - total) !== 0) return { label: 'Borrador · incompleto', cls: 'bg-amber-100 text-amber-800' }
    return { label: 'Listo para asignar', cls: 'bg-blue-100 text-blue-800' }
  }

  const entregableDesc = (e, plan) => {
    if (e.regla_tipo === 'parcialidad') {
      const p = (plan.pagos_planes_parcialidades || []).find((x) => x.orden === e.hasta_orden)
      return `se libera al cubrir la parcialidad ${e.hasta_orden}${p ? ` (${p.nombre})` : ''}`
    }
    const cName = conceptos.find((c) => c.id === e.concepto_id)?.nombre || 'el concepto'
    return `se libera al pagar ${cName}`
  }

  // Agrupa conceptos iguales: "Mensualidad ×4 — $2,000" en vez de 4 renglones repetidos
  const agruparConceptos = (items) =>
    Object.values((items || []).reduce((acc, it) => {
      const name = it.pagos_conceptos?.nombre || 'Concepto'
      if (!acc[name]) acc[name] = { name, count: 0, total: 0 }
      acc[name].count += 1
      acc[name].total = round2(acc[name].total + Number(it.monto || 0))
      return acc
    }, {}))

  const periodoPlan = (parc) => {
    const fechas = parc.map((p) => p.fecha_limite).filter(Boolean).sort()
    if (!fechas.length) return 'Sin fechas'
    if (fechas.length === 1) return formatDate(fechas[0])
    return `${formatDate(fechas[0])} – ${formatDate(fechas[fechas.length - 1])}`
  }

  const term = search.trim().toLowerCase()
  const planesFiltrados = term
    ? planes.filter((pl) =>
        (pl.nombre || '').toLowerCase().includes(term) ||
        (pl.descripcion || '').toLowerCase().includes(term)
      )
    : planes

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2 flex-nowrap">
        <div className="flex-1 min-w-0 relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar plan..."
            className="pl-10"
          />
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-muted-foreground hidden sm:block whitespace-nowrap">
            <span className="font-medium text-foreground">{planesFiltrados.length}</span>{' '}
            {planesFiltrados.length === 1 ? 'plan' : 'planes'}
          </span>
          <Button onClick={openNuevo} className="h-10" aria-label="Nuevo plan" title="Nuevo plan">
            <Plus className="h-4 w-4 sm:mr-2" />
            <span className="hidden sm:inline">Nuevo plan</span>
          </Button>
        </div>
      </div>
      {temporadaSelector && (
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-muted-foreground">Temporada</span>
          {temporadaSelector}
        </div>
      )}

      {loading ? (
        <p className="py-8 text-center text-sm text-muted-foreground">Cargando...</p>
      ) : planesFiltrados.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center">
            <p className="text-sm text-muted-foreground">Aún no hay planes de pago en esta temporada.</p>
            <Button className="mt-3" size="sm" onClick={openNuevo}>
              <Plus className="mr-2 h-4 w-4" /> Crear plan
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {planesFiltrados.map((plan) => {
            const isExpanded = expandedPlan === plan.id
            const items = plan.pagos_planes_items || []
            const parcialidades = (plan.pagos_planes_parcialidades || []).slice().sort((a, b) => a.orden - b.orden)
            const entregables = plan.pagos_planes_entregables || []
            const total = totalPlan(plan)
            const estado = planEstado(plan)
            const conceptosAgrupados = agruparConceptos(items)

            return (
              <Card key={plan.id}>
                <CardContent className="pt-4">
                  <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                    {/* Encabezado clickeable (expandir) */}
                    <button
                      type="button"
                      className="flex flex-1 items-start gap-2 text-left"
                      onClick={() => setExpandedPlan(isExpanded ? null : plan.id)}
                    >
                      {isExpanded ? <ChevronUp className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /> : <ChevronDown className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />}
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="font-medium">{plan.nombre}</span>
                          <Badge className={`text-xs ${estado.cls}`}>{estado.label}</Badge>
                          {etiquetaDesfase(plan.id) && (
                            <Badge className="bg-amber-100 text-xs text-amber-800">{etiquetaDesfase(plan.id)}</Badge>
                          )}
                        </div>
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {formatMoney(total)} · {parcialidades.length} {parcialidades.length === 1 ? 'pago' : 'pagos'} · {scopeLabel(plan.scope)}
                        </p>
                      </div>
                    </button>

                    {/* Acciones: 1 primary + outline + dropdown */}
                    <div className="flex items-center gap-1 self-end sm:self-start">
                      <Button size="sm" onClick={() => openEditar(plan)}>
                        <Pencil className="mr-1 h-3.5 w-3.5" /> Editar
                      </Button>
                      {/* Un plan a medias no se ofrece: asignarPlan lo rechaza de
                          todas formas, pero hasta el final -- despues de elegir
                          jugadores. Mejor decirlo aqui. */}
                      <Button variant="outline" size="sm" onClick={() => verAsignaciones(plan)}
                        disabled={!planListoParaAsignar(plan)}
                        title={planListoParaAsignar(plan) ? '' : 'Completa el plan antes de asignarlo: necesita conceptos y un calendario de pagos que cuadre.'}>
                        <ClipboardList className="mr-1 h-3.5 w-3.5" /> Asignar
                      </Button>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-9 w-9 p-0">
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => openRecalc(plan)}>
                            <RefreshCw className="mr-2 h-4 w-4" /> Actualizar cargos{totalDesfase(plan.id) > 0 ? ` (${totalDesfase(plan.id)})` : ''}
                          </DropdownMenuItem>
                          <DropdownMenuItem className="text-destructive" onClick={() => handleDeletePlan(plan)}>
                            <Trash2 className="mr-2 h-4 w-4" /> Eliminar
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>
                  </div>

                  {isExpanded && (
                    <div className="mt-4 space-y-4 border-t pt-4">
                      {/* Resumen en tira de stats */}
                      <div className="grid grid-cols-2 gap-px overflow-hidden rounded-lg border bg-border sm:grid-cols-4">
                        <Stat label="Total" value={formatMoney(total)} />
                        <Stat label="Pagos" value={parcialidades.length} />
                        <Stat label="Periodo" value={periodoPlan(parcialidades)} />
                        <Stat label="Alcance" value={scopeLabel(plan.scope)} />
                      </div>

                      <div className="grid gap-x-8 gap-y-4 lg:grid-cols-2">
                        {/* Conceptos (agrupados) */}
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Conceptos</p>
                          {conceptosAgrupados.length === 0 ? (
                            <p className="text-sm text-muted-foreground">Sin conceptos.</p>
                          ) : (
                            <ul className="text-sm">
                              {conceptosAgrupados.map((c) => (
                                <li key={c.name} className="flex items-center justify-between gap-3 py-1">
                                  <span className="min-w-0 truncate">
                                    {c.name}{c.count > 1 && <span className="text-muted-foreground"> ×{c.count}</span>}
                                  </span>
                                  <span className="shrink-0 tabular-nums">{formatMoney(c.total)}</span>
                                </li>
                              ))}
                              <li className="mt-1 flex items-center justify-between gap-3 border-t pt-1.5 font-semibold">
                                <span>Total</span><span className="tabular-nums">{formatMoney(total)}</span>
                              </li>
                            </ul>
                          )}
                        </div>

                        {/* Calendario de pagos (línea de tiempo) */}
                        <div>
                          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">Calendario de pagos</p>
                          {parcialidades.length === 0 ? (
                            <p className="text-sm text-muted-foreground">Sin parcialidades. Edita el plan para definir en cuántos pagos se cubre.</p>
                          ) : (
                            <ul className="space-y-1.5">
                              {parcialidades.map((p) => (
                                <li key={p.id} className="flex items-center gap-3 text-sm">
                                  <span className="shrink-0 rounded-md bg-muted px-2 py-1 text-xs font-medium tabular-nums text-muted-foreground">
                                    {p.fecha_limite ? formatDate(p.fecha_limite) : 'sin fecha'}
                                  </span>
                                  <span className="min-w-0 flex-1 truncate">{p.nombre}</span>
                                  <span className="shrink-0 font-medium tabular-nums">{formatMoney(p.monto)}</span>
                                </li>
                              ))}
                            </ul>
                          )}
                        </div>
                      </div>

                      {/* Entregables (callout) */}
                      {entregables.length > 0 && (
                        <div className="space-y-1.5">
                          <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">Entregables</p>
                          {entregables.map((e) => (
                            <div key={e.id} className="flex items-start gap-2 rounded-lg border bg-muted/40 p-2.5 text-sm">
                              <Package className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                              <span>
                                <span className="font-medium">{e.nombre}</span>
                                <span className="text-muted-foreground"> — {entregableDesc(e, plan)}</span>
                              </span>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}
                </CardContent>
              </Card>
            )
          })}
        </div>
      )}

      <PlanEditorWizard
        open={editorOpen}
        onOpenChange={setEditorOpen}
        plan={editingPlan}
        temporadaId={temporadaId}
        temporadas={temporadas}
        conceptosCatalogo={conceptos}
        onSaved={onPlanGuardado}
      />

      <ConfirmDialog />

      {/* Recalcular cargos */}
      <Dialog open={recalc.open} onOpenChange={(v) => { if (!v) closeRecalc() }}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Actualizar cargos</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-muted-foreground">
              {recalc.desdeGuardado
                ? 'Cambiaste los montos del plan. Los cargos ya generados siguen con los montos anteriores. Lo ya pagado no se toca.'
                : 'Actualiza los cargos ya generados a los montos actuales del plan. No toca lo ya pagado.'}
            </p>
            <div className="grid gap-2">
              <button type="button" role="radio" aria-checked={recalc.mode === 'pendiente'}
                onClick={() => changeRecalcMode('pendiente')}
                className={`rounded-lg border p-3 text-left text-sm ${recalc.mode === 'pendiente' ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/50'}`}>
                <span className="font-medium">Solo pendientes</span>
                <p className="text-xs text-muted-foreground">No toca a quien ya pagó (total o parcial).</p>
              </button>
              <button type="button" role="radio" aria-checked={recalc.mode === 'total'}
                onClick={() => changeRecalcMode('total')}
                className={`rounded-lg border p-3 text-left text-sm ${recalc.mode === 'total' ? 'border-primary ring-1 ring-primary' : 'hover:bg-muted/50'}`}>
                <span className="font-medium">Todos</span>
                <p className="text-xs text-muted-foreground">Recalcula todos. Si alguien pagó de más, el sobrante va a saldo a favor.</p>
              </button>
            </div>
            <div className="rounded-lg border bg-muted/30 p-3 text-sm">
              {recalc.loading ? (
                <p className="text-muted-foreground">Calculando impacto...</p>
              ) : !recalc.preview || recalc.preview.total === 0 ? (
                <p className="text-muted-foreground">Ningún cargo cambia de monto con esta opción.</p>
              ) : (
                <div className="space-y-1">
                  <p><strong>{recalc.preview.total}</strong> cargo(s) cambiarán.</p>
                  {recalc.mode === 'total' && recalc.preview.saldoAFavor > 0 && (
                    <p className="text-emerald-700">Se generará {formatMoney(recalc.preview.saldoAFavor)} de saldo a favor.</p>
                  )}
                  <ul className="mt-1 max-h-40 space-y-0.5 overflow-y-auto text-xs text-muted-foreground">
                    {recalc.preview.afectados.slice(0, 8).map((a, i) => (
                      <li key={i} className="flex justify-between gap-2">
                        <span className="truncate">{a.nombre}</span>
                        <span className="shrink-0 tabular-nums">{formatMoney(a.antes)} → {formatMoney(a.despues)}</span>
                      </li>
                    ))}
                    {recalc.preview.afectados.length > 8 && <li>…y {recalc.preview.afectados.length - 8} más</li>}
                  </ul>
                </div>
              )}
            </div>

            {/* Cargos que nunca se emitieron: pasa al agregarle un pago a un plan
                que ya cobraba. El selector de arriba no aplica aqui -- estos no
                existen, se crean. */}
            {!recalc.loading && recalc.faltantes?.total > 0 && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-sm">
                <p className="font-medium text-amber-900">
                  {recalc.faltantes.total} cargo(s) por emitir · {formatMoney(recalc.faltantes.monto)}
                </p>
                <p className="mt-0.5 text-xs text-amber-800">
                  Le agregaste pagos al plan después de asignarlo. Se generarán para {recalc.faltantes.jugadores} jugador(es) que ya lo tienen.
                </p>
                {recalc.faltantes.omitidosPorCancelacion > 0 && (
                  <p className="mt-1 text-xs text-amber-800">
                    {recalc.faltantes.omitidosPorCancelacion} no se emitirán porque fueron cancelados a propósito.
                  </p>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={closeRecalc}>{recalc.desdeGuardado ? 'Después' : 'Cancelar'}</Button>
            <Button onClick={applyRecalc} disabled={recalc.applying || recalc.loading || (!recalc.preview?.total && !recalc.faltantes?.total)}>
              {recalc.applying ? 'Actualizando...' : 'Actualizar cargos'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Result toast */}
      {applyResult && (
        <div className={`fixed bottom-4 right-4 z-50 rounded-lg px-4 py-3 text-sm font-medium shadow-lg ${applyResult.type === 'error' ? 'bg-destructive text-destructive-foreground' : 'bg-green-600 text-white'}`}>
          {applyResult.message}
        </div>
      )}
    </div>
  )
}

export default PlanesTab
